import { computed, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
// Feature services, models
import { INITIAL_STATE } from './user-partner-context.state'
import { UserPartnerContextClientService } from '../client'
import type { CdbUserContextDto, CdbUserContextPartnerInfoDto } from '../models'
import { StorageEnum } from '../enums'
// HUB modules
import { StorageService } from '@inside-hub-app/hub-storage'
import { HubConfigurationService } from '@inside-hub-app/hub-configuration'
// 3rd party modules
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals'
import { lastValueFrom } from 'rxjs'
import { NGXLogger } from 'ngx-logger'

/**
 * Store for managing user and partner context throughout the application.
 *
 * Provides centralized state management for the current user and their selected partner,
 * including persistence to storage and synchronization with the backend API.
 * @example
 * ```typescript
 * const store = inject(UserPartnerContextStore)
 *
 * // Access current state
 * const user = store.currentUser()
 * const partner = store.currentPartner()
 *
 * // Observe changes
 * store.observeUserChange$.subscribe(user => { ... })
 * store.observePartnerChange$.subscribe(partner => { ... })
 * ```
 */
export const UserPartnerContextStore = signalStore(
  { providedIn: 'root' },
  withState(INITIAL_STATE),
  withProps(({ _currentUser, _currentPartner }) => ({
    _storageService: inject(StorageService),
    _hubConfigurationService: inject(HubConfigurationService),
    _logger: inject(NGXLogger),
    _userPartnerContextClientService: inject(UserPartnerContextClientService),
    observeUserChange$: toObservable(_currentUser),
    observePartnerChange$: toObservable(_currentPartner)
  })),
  withComputed(({ _currentUser, _currentPartner }) => ({
    currentUser: computed(() => _currentUser()),
    currentPartner: computed(() => _currentPartner())
  })),
  withMethods(({ _currentUser, _currentPartner, _storageService, _userPartnerContextClientService, _logger, ...store }) => {
    async function _setUser (user: CdbUserContextDto | null): Promise<void> {
      try {
        await lastValueFrom(_storageService.set<number>(StorageEnum.PREFIX + StorageEnum.CURRENT_USER_ID, user.id))
        patchState(store, { _currentUser: user })
      } catch (error) {
        _logger.error('[UserPartnerContextStore] Setting user in storage failed:', error)
        patchState(store, { _currentUser: null })
      }
    }

    async function _setPartner (partner: CdbUserContextPartnerInfoDto | null): Promise<void> {
      try {
        await lastValueFrom(_storageService.set<number>(StorageEnum.PREFIX + StorageEnum.CURRENT_PARTNER_ID, partner.id))
        patchState(store, { _currentPartner: partner })
      } catch (error) {
        _logger.error('[UserPartnerContextStore] Setting partner in storage failed:', error)
        patchState(store, { _currentPartner: null })
      }
    }

    async function _loadContextDataFromAPI (
      userId: number,
      partnerId: number
    ): Promise<{ user: CdbUserContextDto, partner: CdbUserContextPartnerInfoDto | null }> {
      try {
        const user = await lastValueFrom(_userPartnerContextClientService.getUserContext(userId))
        const partner = user.partners.find(partner => partner.id === partnerId)

        await Promise.all([_setUser(user), _setPartner(partner)])
        return { user, partner }
      } catch (error) {
        _logger.error('[UserPartnerContextStore] Loading context data from API failed:', error)
        await Promise.all([_setUser(null), _setPartner(null)])
        return null
      }
    }

    async function _loadContextDataFromStorage (): Promise<{ userId: number, partnerId: number } | null> {
      try {
        const [storedUserId, storedPartnerId] = await Promise.all([
          lastValueFrom(_storageService.get<number>(StorageEnum.PREFIX + StorageEnum.CURRENT_USER_ID)),
          lastValueFrom(_storageService.get<number>(StorageEnum.PREFIX + StorageEnum.CURRENT_PARTNER_ID))
        ])

        if (storedUserId && storedPartnerId) {
          return { userId: storedUserId, partnerId: storedPartnerId }
        } else {
          patchState(store, { _currentUser: null, _currentPartner: null })
          return null
        }
      } catch (error) {
        _logger.error('[UserPartnerContextStore] Loading context data from storage failed:', error)
        patchState(store, { _currentUser: null, _currentPartner: null })
        return null
      }
    }

    return {
      _setUser,
      _setPartner,
      _loadContextDataFromAPI,
      _loadContextDataFromStorage
    }
  }),
  // Methods to interact with the store
  withMethods(({ _loadContextDataFromAPI, _loadContextDataFromStorage, _setUser, _setPartner, ...store }) => ({
    /**
     * Initializes the user and partner context from the API.
     * Should be called after successful authentication.
     * @param userId - The authenticated user's ID
     * @param partnerId - The selected partner ID
     * @returns Promise that resolves with the loaded user and partner context
     */
    initializeUserPartnerContext: async (
      userId: number,
      partnerId: number
    ): Promise<{ user: CdbUserContextDto, partner: CdbUserContextPartnerInfoDto | null }> => {
      return await _loadContextDataFromAPI(userId, partnerId)
    },

    /**
     * Refreshes the user and partner context from the API.
     * Only refreshes if the provided IDs match the current context.
     * Use this to ensure the latest data without changing context.
     * If no IDs are provided, it refreshes based on the current context.
     * @param userId - The user ID to refresh
     * @param partnerId - The partner ID to refresh
     * @returns Promise that resolves when refresh is complete (or skipped if IDs don't match)
     */
    refreshUserPartnerContext: async (userId?: number, partnerId?: number): Promise<void> => {
      const currentUserId = store.currentUser()?.id ?? null
      const currentPartnerId = store.currentPartner()?.id ?? null

      if (!userId || !partnerId) {
        await _loadContextDataFromAPI(currentUserId, currentPartnerId)
        return
      }

      if (userId === currentUserId && partnerId === currentPartnerId) {
        await _loadContextDataFromAPI(userId, partnerId)
      }
    },

    /**
     * Rehydrates the user and partner id's from storage and load context from API.
     * Should be called on application startup to restore previous session.
     * @returns Promise that resolves when rehydration is complete
     */
    rehydrateUserPartnerContextState: async (): Promise<void> => {
      const storageData = await _loadContextDataFromStorage()
      if (storageData) {
        await _loadContextDataFromAPI(storageData.userId, storageData.partnerId)
      }
    },

    /**
     * Updates only the user context, keeping partner unchanged.
     * @param user - The user context to set
     * @returns Promise that resolves when update is complete
     */
    updateUser: async (user: CdbUserContextDto | null): Promise<void> => {
      await _setUser(user)
    },

    /**
     * Updates only the partner context, keeping user unchanged.
     * @param partner - The partner context to set
     * @returns Promise that resolves when update is complete
     */
    updatePartner: async (partner: CdbUserContextPartnerInfoDto | null): Promise<void> => {
      await _setPartner(partner)
    }
  }))
)
